import { useState, useMemo, useEffect } from 'react';

/**
 * Custom hook for sortable tables
 * @param {Array} data - Array of objects to sort
 * @param {Object} config - Initial sort configuration { key: string, direction: 'asc' | 'desc' }
 * @returns {Object} - { items, requestSort, sortConfig }
 */
export const useSortableTable = (data, initialConfig = null) => {
  const [sortConfig, setSortConfig] = useState(initialConfig);

  // Ensure sort config is set when data first loads
  useEffect(() => {
    if (data && data.length > 0 && initialConfig && !sortConfig) {
      setSortConfig(initialConfig);
    }
  }, [data, initialConfig, sortConfig]);

  const sortedItems = useMemo(() => {
    if (!data) return [];
    
    let sortableItems = [...data];
    
    console.log('[useSortableTable] Sorting with config:', sortConfig, 'Data length:', data.length);
    
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const aValue = getNestedValue(a, sortConfig.key);
        const bValue = getNestedValue(b, sortConfig.key);
        
        // Handle null/undefined
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // Handle dates
        if (isDate(aValue) || isDate(bValue)) {
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);
          return sortConfig.direction === 'asc' 
            ? aDate - bDate 
            : bDate - aDate;
        }
        
        // Handle numbers
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
        
        // Handle strings (case-insensitive)
        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();
        
        if (aString < bString) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aString > bString) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    
    console.log('[useSortableTable] Sorted result (first 3):', sortableItems.slice(0, 3).map(item => item[sortConfig?.key]));
    
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (columnKey) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return { items: sortedItems, requestSort, sortConfig, getSortIndicator };
};

// Helper function to get nested object values (e.g., "customer.name")
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Helper to detect if a value is a date
const isDate = (value) => {
  if (!value) return false;
  // Don't treat pure numbers or strings that are all digits as dates
  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return false;
  }
  // Don't treat strings with hyphens as dates unless they look like ISO dates (YYYY-MM-DD)
  if (typeof value === 'string' && value.includes('-') && !/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return false;
  }
  const date = new Date(value);
  return date instanceof Date && !isNaN(date);
};

export default useSortableTable;
